import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { MAX_REQUEST_NOTE_LENGTH, REQUEST_TYPE } from '@hotel-voice/shared';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, ListSkeleton, SuccessState, UnavailableState } from '@/components/common/StateView';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { useSubmitRequest } from '@/hooks/useSubmitRequest';
import { formatServicePrice } from '@/lib/money';
import { cn } from '@/lib/utils';
import { selectedLaundry, toRequestItems, toggleLaundryService, type LaundrySelection } from './selection';

/**
 * Laundry: tick the services, add a note, place the order. No counts and no
 * weights — the price per item or per kg is shown so the guest knows what to
 * expect, and staff settle the charge when the clothes are collected.
 */
export function LaundryPage() {
  const { services, servicesPhase, servicesError, reloadServices } = useReadyGuest();
  const { submitting, result, submit, reset } = useSubmitRequest(REQUEST_TYPE.LAUNDRY);

  const [selection, setSelection] = useState<LaundrySelection>(new Set());
  const [note, setNote] = useState('');

  const catalogue = useMemo(() => services?.laundry ?? [], [services]);
  const selected = useMemo(() => selectedLaundry(selection, catalogue), [selection, catalogue]);

  const placeOrder = async () => {
    if (selected.length === 0 || submitting) return;
    const ok = await submit({ items: toRequestItems(selected), note });
    if (ok) {
      setSelection(new Set());
      setNote('');
    }
  };

  const body = () => {
    if (servicesPhase === 'loading' || servicesPhase === 'idle') return <ListSkeleton rows={3} />;
    if (servicesPhase === 'error') {
      return <ErrorState message={servicesError ?? 'Could not load laundry services.'} onRetry={reloadServices} />;
    }
    if (!services?.flags.laundryService) return <UnavailableState service="Laundry service" />;

    if (result) {
      return (
        <SuccessState
          title="Laundry order placed"
          message="We’ll collect your clothes from your room shortly. The charge is settled once they’re counted."
        >
          <Button onClick={reset}>Place another order</Button>
        </SuccessState>
      );
    }

    if (catalogue.length === 0) {
      return <EmptyState title="No laundry services listed" message="Please call reception for laundry." />;
    }

    return (
      <div className="flex flex-col gap-4 p-4">
        <Card className="gap-0 py-0">
          <ul className="divide-y">
            {catalogue.map((service) => {
              const checked = selection.has(service.id);
              const inputId = `ld-${service.id}`;
              return (
                <li key={service.id} className={cn('flex items-center gap-3 px-4 py-3', checked && 'bg-primary/5')}>
                  <Checkbox
                    id={inputId}
                    checked={checked}
                    onCheckedChange={() => setSelection((s) => toggleLaundryService(s, service.id))}
                    disabled={submitting}
                  />
                  <Label htmlFor={inputId} className="flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-3">
                    <span className="text-sm font-medium">{service.name}</span>
                    <Badge variant={service.type === 'paid' ? 'default' : 'success'} className="shrink-0">
                      {formatServicePrice(service)}
                    </Badge>
                  </Label>
                </li>
              );
            })}
          </ul>
        </Card>

        <div className="bg-tile-blue text-foreground flex gap-2 rounded-xl px-3 py-2.5 text-xs">
          <Info className="text-primary mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <p>
            The final charge is confirmed when your laundry is collected.
          </p>
        </div>

        <Card>
          <CardContent className="flex flex-col gap-2">
            <Label htmlFor="ld-note">Note for laundry</Label>
            <Textarea
              id="ld-note"
              placeholder="e.g. Please collect clothes from the room after 11 am"
              value={note}
              maxLength={MAX_REQUEST_NOTE_LENGTH}
              onChange={(event) => setNote(event.target.value)}
              disabled={submitting}
            />
          </CardContent>
        </Card>

        {selected.length > 0 && (
          <Card>
            <CardContent className="flex flex-col gap-2 text-sm">
              <p className="font-semibold">Selected services</p>
              {selected.map((service) => (
                <div key={service.id} className="flex justify-between gap-3">
                  <span>{service.name}</span>
                  <span className="text-muted-foreground tabular-nums">{formatServicePrice(service)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Button size="lg" onClick={placeOrder} disabled={selected.length === 0 || submitting} aria-busy={submitting}>
          {submitting ? 'Placing order…' : 'Place Order'}
        </Button>
      </div>
    );
  };

  return (
    <AppShell>
      <PageHeader title="Laundry" subtitle="Wash, dry clean & iron" />
      {body()}
    </AppShell>
  );
}
